import React, { useState } from 'react';
import { Modal } from 'antd';
import { CFT160, CFT162, CFT163, t20aApi } from '../../../services/t20aApi';
import { dateToInput, inputToDate } from '../helpers';

interface Tab3Props {
  formData: Partial<CFT160>;
  agendas: Partial<CFT162>[];
  isEditing: boolean;
  addAgendaRow: () => void;
  deleteAgendaRow: (idx: number) => void;
  setAgendaField: (idx: number, key: keyof CFT162, value: CFT162[keyof CFT162]) => void;
  addTrackingItem: (agendaIdx: number) => void;
  deleteTrackingItem: (agendaIdx: number, trackIdx: number) => void;
  setTrackingField: (agendaIdx: number, trackIdx: number, key: keyof CFT163, value: CFT163[keyof CFT163]) => void;
  selectedAgendaIdx: number;
  setSelectedAgendaIdx: (idx: number) => void;
}

/** Tab 3: Meeting Content -- Agendas (CFT162) with nested Tracking Items (CFT163) */
const Tab3Agenda: React.FC<Tab3Props> = React.memo(({
  formData, agendas, isEditing,
  addAgendaRow, deleteAgendaRow, setAgendaField,
  addTrackingItem, deleteTrackingItem, setTrackingField,
  selectedAgendaIdx, setSelectedAgendaIdx,
}) => {
  const [trackingModalVisible, setTrackingModalVisible] = useState(false);
  const [trackingAgendaIdx, setTrackingAgendaIdx] = useState(-1);

  const openTrackingModal = (agendaIdx: number) => {
    setTrackingAgendaIdx(agendaIdx);
    setTrackingModalVisible(true);
  };

  const currentAgenda = trackingAgendaIdx >= 0 ? agendas[trackingAgendaIdx] : undefined;
  const trackingItems = currentAgenda?.tracking_items || [];

  return (
    <div>
      <div className="form-grid" style={{ marginBottom: 12 }}>
        <label>會議事項</label>
        <input
          type="number"
          value={formData.ft160_19 ?? 0}
          readOnly
          style={{ width: 80 }}
        />
      </div>

      <div className="table-wrapper">
        <div className="table-toolbar">
          <div className="left">
            <span style={{ fontWeight: 500, fontSize: 13 }}>議題明細</span>
          </div>
          <div className="right">
            <button className="btn btn-sm" disabled={!isEditing} onClick={addAgendaRow}>明細新增</button>
            <button className="btn btn-sm btn-danger" disabled={!isEditing || agendas.length === 0}
              onClick={() => deleteAgendaRow(selectedAgendaIdx)}>明細刪除</button>
          </div>
        </div>
        <div className="table-scroll" style={{ maxHeight: 340 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>序號</th>
                <th style={{ minWidth: 200 }}>討論內容</th>
                <th style={{ minWidth: 200 }}>決議事項</th>
                <th style={{ width: 80 }}>追蹤事項</th>
              </tr>
            </thead>
            <tbody>
              {agendas.map((row, idx) => (
                <tr
                  key={idx}
                  className={idx === selectedAgendaIdx ? 'selected' : ''}
                  onClick={() => setSelectedAgendaIdx(idx)}
                >
                  <td>{row.ft162_02}</td>
                  <td>
                    <textarea
                      value={row.ft162_11 || ''}
                      onChange={e => setAgendaField(idx, 'ft162_11', e.target.value)}
                      disabled={!isEditing}
                      rows={2}
                      style={{ width: '100%', fontSize: 13, minHeight: 40 }}
                    />
                  </td>
                  <td>
                    <textarea
                      value={row.ft162_12 || ''}
                      onChange={e => setAgendaField(idx, 'ft162_12', e.target.value)}
                      disabled={!isEditing}
                      rows={2}
                      style={{ width: '100%', fontSize: 13, minHeight: 40 }}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className="btn btn-sm"
                      onClick={(e) => { e.stopPropagation(); openTrackingModal(idx); }}
                    >
                      {(row.tracking_items || []).length > 0
                        ? `(${(row.tracking_items || []).length})`
                        : '查看'}
                    </button>
                  </td>
                </tr>
              ))}
              {agendas.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'rgba(0,0,0,0.25)' }}>無資料</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tracking Items Modal (PWT20AF2) */}
      <Modal
        title={`追蹤事項 — 議題 ${currentAgenda?.ft162_02 || ''}`}
        open={trackingModalVisible}
        onCancel={() => setTrackingModalVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
          <button className="btn btn-sm"
            disabled={!isEditing}
            onClick={() => addTrackingItem(trackingAgendaIdx)}>新增追蹤事項</button>
        </div>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}>序</th>
                <th style={{ minWidth: 150 }}>追蹤事項說明</th>
                <th style={{ width: 110 }}>預計完成日</th>
                <th style={{ width: 110 }}>實際完成日</th>
                <th style={{ width: 90 }}>負責人</th>
                <th style={{ width: 80 }}>負責人姓名</th>
                <th style={{ minWidth: 100 }}>處理情況</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {trackingItems.map((item, tIdx) => (
                <tr key={tIdx}>
                  <td>{item.ft163_03}</td>
                  <td>
                    <textarea
                      value={item.ft163_11 || ''}
                      onChange={e => setTrackingField(trackingAgendaIdx, tIdx, 'ft163_11', e.target.value)}
                      disabled={!isEditing}
                      rows={2}
                      style={{ width: '100%', fontSize: 13, minHeight: 36 }}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={dateToInput(item.ft163_12)}
                      onChange={e => setTrackingField(trackingAgendaIdx, tIdx, 'ft163_12', inputToDate(e.target.value))}
                      disabled={!isEditing}
                      style={{ width: '100%', height: 28, fontSize: 13 }}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={dateToInput(item.ft163_14)}
                      onChange={e => setTrackingField(trackingAgendaIdx, tIdx, 'ft163_14', inputToDate(e.target.value))}
                      disabled={!isEditing}
                      style={{ width: '100%', height: 28, fontSize: 13 }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.ft163_15 || ''}
                      onChange={e => setTrackingField(trackingAgendaIdx, tIdx, 'ft163_15', e.target.value)}
                      onBlur={async () => {
                        const code = item.ft163_15 || '';
                        if (!code) return;
                        const name = await t20aApi.lookupName('employee', code);
                        setTrackingField(trackingAgendaIdx, tIdx, 'ft163_15a', name);
                        const dept = await t20aApi.lookupName('employee_dept', code);
                        setTrackingField(trackingAgendaIdx, tIdx, 'ft163_16a', dept);
                      }}
                      disabled={!isEditing}
                      style={{ width: '100%', height: 28, fontSize: 13 }}
                    />
                  </td>
                  <td style={{ fontSize: 12 }}>{item.ft163_15a || ''}</td>
                  <td>
                    <textarea
                      value={item.ft163_13 || ''}
                      onChange={e => setTrackingField(trackingAgendaIdx, tIdx, 'ft163_13', e.target.value)}
                      disabled={!isEditing}
                      rows={2}
                      style={{ width: '100%', fontSize: 13, minHeight: 36 }}
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      disabled={!isEditing}
                      onClick={() => deleteTrackingItem(trackingAgendaIdx, tIdx)}
                      title="刪除"
                    >
                      X
                    </button>
                  </td>
                </tr>
              ))}
              {trackingItems.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'rgba(0,0,0,0.25)' }}>無追蹤事項</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
});

Tab3Agenda.displayName = 'Tab3Agenda';
export default Tab3Agenda;
